<script setup>
import { ref, onMounted, computed, watch } from 'vue';
import { useRouter } from 'vue-router';
import { toast } from 'vue3-toastify';

const router = useRouter();
const isLoading = ref(true);
const isSaving = ref(false);
const saveLoader = ref(false);

// Configuration state
const prefix = ref('');
const separator = ref('');
const proNumber = ref('');
const firstProNumber = ref('');
const lastProNumber = ref('');
const proNumberRange = ref(null);
const suffix = ref('');

// Validation state
const prefixError = ref('');
const separatorError = ref('');
const suffixError = ref('');


// Add these to your existing script setup
const showAddModal = ref(false);
const proNumberQuantity = ref(100);
const isAddingNumbers = ref(false);

// Function to open the modal
const openAddProNumbersModal = () => {
	proNumberQuantity.value = 100; // Default quantity
	showAddModal.value = true;
};

// Function to close the modal
const closeAddModal = () => {
	showAddModal.value = false;
};

// Preview of the new range
const getNewRangePreview = computed(() => {
	const quantity = parseInt(proNumberQuantity.value) || 0;
	if (!lastProNumber.value || quantity <= 0) {
		return 'N/A';
	}

	// Calculate the new last PRO number
	const currentLast = parseInt(lastProNumber.value);
	const newLast = currentLast + quantity;

	return `${parseInt(lastProNumber.value) + 1} — ${newLast}`;
});

// Function to add more PRO numbers (stub for now)
const addProNumbers = async () => {
	if (!proNumberQuantity.value || proNumberQuantity.value < 1) {
		toast.error('Please enter a valid quantity');
		return;
	}

	isAddingNumbers.value = true;

	try {
		// This will be replaced with the actual API call
		const response = await fetch(`${import.meta.env['PUBLIC_API_URL']}/admin/pro-numbers/add`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
			},
			body: JSON.stringify({
				count: parseInt(proNumberQuantity.value)
			})
		});

		if (!response.ok) {
			throw new Error('Failed to add PRO numbers');
		}

		// Assuming the API returns the updated range
		const data = await response.json();

		// Update the local state with new data
		if (data.firstProNumber) firstProNumber.value = data.firstProNumber;
		if (data.lastProNumber) lastProNumber.value = data.lastProNumber;

		toast.success(`Successfully added ${proNumberQuantity.value} PRO numbers`);
		closeAddModal();

		isLoading.value = true;
		await Promise.all([fetchConfig().catch((err) => undefined), fetchNextProNumber().catch((err) => undefined), getRangeForProNumber().catch((err) => undefined)]);
		isLoading.value = false;

	} catch (error) {
		console.error('Error adding PRO numbers:', error);
		toast.error('Failed to add PRO numbers');
	} finally {
		isAddingNumbers.value = false;
	}
};

// Fetch the first PRO number
const fetchFirstProNumber = async () => {
	try {
		// Replace with actual API endpoint when available
		const response = await fetch(`${import.meta.env['PUBLIC_API_URL']}/admin/pro-numbers/latest?proRange=first`, {
			method: 'GET',
			headers: {
				'Content-Type': 'application/json',
				'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
			}
		});

		if (!response.ok) {
			throw new Error('Failed to fetch PRO number');
		}

		const data = await response.json();
		firstProNumber.value = data.proNumber || '11011'; // Default value if not returned
		return data.proNumber; // Return the first PRO number
	} catch (error) {
		console.error('Error fetching PRO number:', error);
		toast.error('Failed to load latest PRO number');
		// Use default value if fetch fails
		firstProNumber.value = '11011';
		return '11011'; // Return default value
	}
};

// Fetch the last PRO number
const fetchLastProNumber = async () => {
	try {
		// Replace with actual API endpoint when available
		const response = await fetch(`${import.meta.env['PUBLIC_API_URL']}/admin/pro-numbers/latest?proRange=last`, {
			method: 'GET',
			headers: {
				'Content-Type': 'application/json',
				'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
			}
		});

		if (!response.ok) {
			throw new Error('Failed to fetch PRO number');
		}

		const data = await response.json();
		lastProNumber.value = data.proNumber || '11011'; // Default value if not returned
		return data.proNumber; // Return the last PRO number
	} catch (error) {
		console.error('Error fetching PRO number:', error);
		toast.error('Failed to load latest PRO number');
		// Use default value if fetch fails
		lastProNumber.value = '11011';
		return '11011'; // Return default value
	}
};

const getRangeForProNumber = async () => {
	const [first, last] = await Promise.all([fetchFirstProNumber(), fetchLastProNumber()]);
	proNumberRange.value = {
		first: first,
		last: last
	};
}

// Fetch the next PRO number
const fetchNextProNumber = async () => {
	try {
		// Replace with actual API endpoint when available
		const response = await fetch(`${import.meta.env['PUBLIC_API_URL']}/admin/pro-numbers/latest?proRange=next`, {
			method: 'GET',
			headers: {
				'Content-Type': 'application/json',
				'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
			}
		});

		if (!response.ok) {
			throw new Error('Failed to fetch PRO number');
		}

		const data = await response.json();
		proNumber.value = data.proNumber || '11011'; // Default value if not returned
	} catch (error) {
		console.error('Error fetching PRO number:', error);
		toast.error('Failed to load latest PRO number');
		// Use default value if fetch fails
		proNumber.value = '11011';
	} finally {
		isLoading.value = false;
	}
};

// Fetch configuration if it exists
const fetchConfig = async () => {
	try {
		const response = await fetch(`${import.meta.env['PUBLIC_API_URL']}/admin/pro-numbers/config`, {
			method: 'GET',
			headers: {
				'Content-Type': 'application/json',
				'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
			}
		});

		if (response.ok) {
			let data = await response.json();
			if (data && data?.config) {
				data = data.config;
			}
			prefix.value = data.prefix || '';
			separator.value = data.separator || '';
			suffix.value = data.suffix || '';
		}

		if (!validateSeparator(separator.value) || !prefix.value.trim() || !suffix.value.trim()) {
			isSaving.value = false; // Disable save button if validation fails
		} else {
			isSaving.value = true; // Enable save button if valid
		}
	} catch (error) {
		console.error('Error fetching config:', error);
		// Use defaults if fetch fails
	}
};

// Validate separator to only allow special characters
const validateSeparator = (value) => {
	// Regex for special characters only (no alphanumeric)
	const specialCharsRegex = /^[^a-zA-Z0-9]*$/;
	return specialCharsRegex.test(value) && value.trim().length == 1;
};

// Watch for changes to separator to validate
watch(separator, (newValue) => {
	if (!validateSeparator(newValue)) {
		separatorError.value = 'Separator can only contain special characters';
		isSaving.value = false; // Disable save button if invalid
	} else {
		separatorError.value = '';
		if (!prefix.value.trim() || !suffix.value.trim()) {
			isSaving.value = false; // Disable save button if prefix or suffix is invalid
		} else {
			isSaving.value = true; // Enable save button if valid
		}
	}
});

watch(suffix, (newValue) => {
	if (!newValue.trim()) {
		isSaving.value = false; // Disable save button if invalid
	} else {
		if (!validateSeparator(separator.value) || !prefix.value.trim()) {
			isSaving.value = false; // Enable save button if valid
		} else {
			isSaving.value = true;
		}
	}
});

watch(prefix, (newValue) => {
	if (!newValue.trim()) {
		isSaving.value = false; // Disable save button if invalid
	} else {
		if (!validateSeparator(separator.value) || !suffix.value.trim()) {
			isSaving.value = false; // Enable save button if valid
		} else {
			isSaving.value = true;
		}
	}
});

// Computed property for formatted PRO number preview
const formattedProNumber = computed(() => {
	return `${prefix.value}${separator.value}${proNumber.value}${separator.value}${suffix.value}`;
});

// Save configuration
const saveConfig = async () => {
	// Validate inputs before saving
	if (!prefix.value.trim()) {
		prefixError.value = 'Prefix cannot be empty';
		return;
	}

	if (!validateSeparator(separator.value)) {
		separatorError.value = 'Separator can only contain special characters';
		return;
	}

	if (!suffix.value.trim()) {
		suffixError.value = 'Suffix cannot be empty';
		return;
	}

	saveLoader.value = true;

	try {
		const response = await fetch(`${import.meta.env['PUBLIC_API_URL']}/admin/pro-numbers/config`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
			},
			body: JSON.stringify({
				prefix: prefix.value.trim(),
				separator: separator.value.trim(),
				suffix: suffix.value.trim()
			})
		});

		if (!response.ok) {
			throw new Error('Failed to save configuration');
		}

		toast.success('PRO Number configuration saved successfully');
	} catch (error) {
		console.error('Error saving configuration:', error);
		toast.error('Failed to save configuration');
	} finally {
		saveLoader.value = false;
	}
};

// Initialize on component mount
onMounted(async () => {
	// Check authentication before loading the page
	const token = localStorage.getItem('auth_token');
	const expiresAt = localStorage.getItem('expiresAt');

	// If no token or token is expired, redirect to login
	if (!token || !expiresAt || new Date(expiresAt).getTime() <= new Date().getTime()) {
		toast.error("Authentication required");
		router.push('/login');
		return;
	}

	// Fetch initial data
	// Added error handling to ensure all fetches are handled gracefully
	await Promise.all([fetchConfig().catch((err) => undefined), fetchNextProNumber().catch((err) => undefined), getRangeForProNumber().catch((err) => undefined)]);
});
</script>

<template>
	<!-- Loading Spinner while loading data -->
	<div v-if="isLoading" class="w-full h-screen flex items-center justify-center bg-page">
		<div class="flex flex-col items-center">
			<div class="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
			<p class="mt-4 text-primary font-monster font-medium">Loading...</p>
		</div>
	</div>

	<!-- Main content - only shown when data is loaded -->
	<div v-else class="w-[calc(100vw-4rem)] flex-grow flex flex-col pl-6 pr-8 py-7 gap-y-8 overflow-y-scroll">
		<div class="p-2 bg-page">
			<h4 class="text-primary font-bold font-monster -ml-2">
				PRO Number Configuration
			</h4>
		</div>

		<div class="bg-white rounded-[14px] shadow-[2px_2px_6px_rgba(187,195,206,0.4)] p-6">
			<div class="max-w-3xl mx-auto">
				<!-- Info text -->
				<div class="mb-8">
					<p class="text-gray-600">Configure the format of your PRO Numbers. The format will be:
						<span class="font-bold">PREFIX + SEPARATOR + PRO NUMBER + SEPARATOR + SUFFIX</span>
					</p>
				</div>

				<!-- Preview card -->
				<div class="mb-8 p-4 bg-gray-50 rounded-lg border border-gray-200">
					<h3 class="text-sm font-medium text-gray-500 mb-2">PREVIEW</h3>
					<div class="text-2xl font-medium text-secondary">{{ formattedProNumber }}</div>
				</div>

				<div class="grid grid-cols-1 md:grid-cols-2 gap-6">
					<!-- PREFIX field -->
					<div>
						<label class="block text-sm font-medium text-gray-700 mb-1">Prefix</label>
						<input v-model="prefix" type="text"
							class="rounded-[10px] px-4 py-3 bg-white border-[1px] border-stroke font-monster border-solid outline-none font-medium w-full"
							placeholder="Enter prefix" />
						<p v-if="prefixError" class="mt-1 text-sm text-red-600">{{ prefixError }}</p>
					</div>

					<!-- SEPARATOR field -->
					<div>
						<label class="block text-sm font-medium text-gray-700 mb-1">Separator (Special Characters
							Only)</label>
						<input v-model="separator" type="text"
							class="rounded-[10px] px-4 py-3 bg-white border-[1px] border-stroke font-monster border-solid outline-none font-medium w-full"
							placeholder="Enter separator" />
						<p v-if="separatorError" class="mt-1 text-sm text-red-600">{{ separatorError }}</p>
					</div>

					<!-- PRO NUMBER display (read-only) -->
					<div>
						<label class="block text-sm font-medium text-gray-700 mb-1">PRO Number</label>
						<input v-model="proNumber" type="text" readonly
							class="rounded-[10px] px-4 py-3 bg-gray-100 border-[1px] border-stroke font-monster border-solid outline-none font-medium w-full cursor-not-allowed" />
						<p class="mt-1 text-sm text-gray-500">This is next available PRO number</p>
					</div>

					<!-- SUFFIX field -->
					<div>
						<label class="block text-sm font-medium text-gray-700 mb-1">Suffix</label>
						<input v-model="suffix" type="text"
							class="rounded-[10px] px-4 py-3 bg-white border-[1px] border-stroke font-monster border-solid outline-none font-medium w-full"
							placeholder="Enter suffix" />
						<p v-if="suffixError" class="mt-1 text-sm text-red-600">{{ suffixError }}</p>
					</div>
				</div>

				<!-- Save button -->
				<div class="mt-8 flex justify-end">
					<button @click="saveConfig" :disabled="!isSaving"
						class="bg-primary hover:bg-primary/90 text-white font-medium py-2 px-6 rounded-lg flex items-center disabled:bg-gray-400 disabled:cursor-not-allowed">
						<span v-if="saveLoader"
							class="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></span>
						Save Configuration
					</button>
				</div>
			</div>
		</div>
		<div class="border border-secondary w-full"></div>
		<div class="bg-white rounded-[14px] shadow-[2px_2px_6px_rgba(187,195,206,0.4)] p-6 mt-6">
			<div class="max-w-3xl mx-auto">
				<!-- Info text -->
				<div class="mb-6">
					<h4 class="text-primary font-bold font-monster mb-2">
						PRO Number Management
					</h4>
					<p class="text-gray-600">
						Current range of PRO numbers in the system. Use the button below to add more PRO numbers when
						needed.
					</p>
				</div>

				<!-- PRO Number Range Display -->
				<div class="mb-8 p-4 bg-gray-50 rounded-lg border border-gray-200">
					<h3 class="text-sm font-medium text-gray-500 mb-2">CURRENT PRO NUMBER RANGE</h3>
					<div class="flex items-center text-xl font-medium text-secondary">
						<div class="bg-gray-100 px-4 py-2 rounded border border-gray-200">
							{{ firstProNumber }}
						</div>
						<div class="mx-3 text-gray-500">—</div>
						<div class="bg-gray-100 px-4 py-2 rounded border border-gray-200">
							{{ lastProNumber }}
						</div>
					</div>
				</div>

				<!-- Add More Button -->
				<div class="flex justify-end">
					<button @click="openAddProNumbersModal"
						class="bg-primary hover:bg-primary/90 text-white font-medium py-2 px-6 rounded-lg flex items-center">
						<svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2" viewBox="0 0 20 20"
							fill="currentColor">
							<path fill-rule="evenodd"
								d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
								clip-rule="evenodd" />
						</svg>
						Add More PRO Numbers
					</button>
				</div>
			</div>
		</div>
	</div>
	 <!-- Modal for adding PRO numbers -->
  <div v-if="showAddModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div class="bg-white rounded-lg p-6 w-full max-w-md mx-4">
      <h3 class="text-xl font-bold mb-4 text-secondary">Add PRO Numbers</h3>
      
      <div class="space-y-4">
        <!-- Quantity input -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">Quantity to Add</label>
          <input 
            v-model="proNumberQuantity" 
            type="number" 
            min="1"
            max="1000"
            class="rounded-[10px] px-4 py-3 bg-white border-[1px] border-stroke font-monster border-solid outline-none font-medium w-full"
            placeholder="Enter quantity (max 1000)" 
          />
        </div>
        
        <!-- PRO Number preview -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">New Range Preview</label>
          <div class="rounded-[10px] px-4 py-3 border-[1px] border-stroke bg-gray-50 font-medium">
            {{ getNewRangePreview }}
          </div>
          <p class="text-xs text-gray-500 mt-1">
            This will add {{ proNumberQuantity || 0 }} new PRO numbers to the system.
          </p>
        </div>
      </div>

      <div class="mt-6 flex justify-end space-x-3">
        <button 
          @click="closeAddModal"
          class="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
        >
          Cancel
        </button>
        <button 
          @click="addProNumbers" 
          :disabled="isAddingNumbers || !proNumberQuantity || proNumberQuantity < 1"
          class="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:bg-gray-400 disabled:cursor-not-allowed"
        >
          <span v-if="isAddingNumbers" class="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></span>
          Add Numbers
        </button>
      </div>
    </div>
  </div>
</template>
